import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { ReEvaluateButton } from '@src/features/ActionPaths/ReEvaluate';
import { AnalyzeRisksButton } from '@src/features/RiskAnalysis/components/buttons/AnalyzeRisks';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import {
  ActionStatusCodesWidget,
  ActionTakenCodesWidget,
  InQueueWidget,
  NotesWidget,
  RecordAgeInPathWidget,
  SourceRecordsRemediatedWidget,
} from '@src/widgets/ActionPaths';
import { FilterCriteriaWidget } from '@src/widgets/Criteria';
import { RemediationActionsWidget } from '@src/widgets/RiskRecords';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import React from 'react';

import { Skeleton } from './components/Skeleton';

/**
 * Layout: Automatic
 *
 * Renders the automatic action path view layout.
 *
 * @param actionPath
 * @param actionPathStatistics
 * @param hasCipSource
 * @param isLoading
 * @returns {Element}
 * @constructor
 */
export function Automatic({ actionPath = {}, actionPathStatistics = {}, hasCipSource = false, isLoading = false }) {
  if (isLoading) {
    return <Skeleton hasCipSource={hasCipSource} />;
  }

  return (
    <ErrorHandler location="src/pages/private/ActionPaths/View/layouts/Automatic/Automatic.jsx">
      <Grid>
        <Grid.Col span={{ xs: '100%' }}>
          <Layout.Group>
            <AnalyzeRisksButton actionPath={actionPath} variant="button" />
            <ReEvaluateButton actionPath={actionPath} />
          </Layout.Group>
        </Grid.Col>
      </Grid>
      <Grid className="flexGrid">
        {hasCipSource
          ? [
              <Grid.Col
                className="grow"
                css={{ paddingTop: 'var(--abyss-space-lg)' }}
                key="cip-InQueue"
                span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
              >
                <InQueueWidget statusCodeCounts={actionPathStatistics?.statusCodeCounts} widgetWrap />
              </Grid.Col>,
              <Grid.Col
                className="grow"
                css={{ paddingTop: 'var(--abyss-space-lg)' }}
                key="cip-ActionStatusCodes"
                span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
              >
                <ActionStatusCodesWidget
                  recordReferenceStatusCodeCounts={actionPathStatistics?.recordReferenceStatusCodeCounts}
                />
              </Grid.Col>,
              <Grid.Col
                className="grow"
                css={{ paddingTop: 'var(--abyss-space-lg)' }}
                key="cip-ActionTakenCodes"
                span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
              >
                <ActionTakenCodesWidget actionTakenCounts={actionPathStatistics?.actionTakenCounts.actionTakenStats} />
              </Grid.Col>,
            ]
          : [
              <Grid.Col
                css={{ paddingTop: 'var(--abyss-space-lg)' }}
                key="InQueue"
                span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
              >
                <InQueueWidget statusCodeCounts={actionPathStatistics?.statusCodeCounts} widgetWrap />
              </Grid.Col>,

              <Grid.Col
                css={{ paddingTop: 'var(--abyss-space-lg)' }}
                key="stats"
                span={{ lg: '66.66%', md: '66.66%', sm: '66.66%', xs: '66.66%' }}
              >
                <Layout.Stack
                  alignItems="top"
                  alignLayout="left"
                  grow
                  space={Number(themeConfiguration?.theme?.space?.lg) || 24}
                >
                  <SourceRecordsRemediatedWidget data={actionPathStatistics?.remediationStats || {}} />
                  <RecordAgeInPathWidget data={actionPathStatistics?.recordAgeStats || {}} />
                </Layout.Stack>
              </Grid.Col>,
            ]}
      </Grid>

      <Grid>
        {actionPath?.manualAssociation === false && [
          <Grid.Col
            css={{ paddingTop: 'var(--abyss-space-lg)' }}
            key="entranceCriteria"
            span={{
              xs: '50%',
            }}
          >
            <FilterCriteriaWidget
              {...{
                context: 'entrance',
                criteria: actionPath?.criteria,
                filterKey: 'entranceCriteria',
              }}
            />
          </Grid.Col>,
          <Grid.Col
            css={{ paddingTop: 'var(--abyss-space-lg)' }}
            key="exitCriteria"
            span={{
              xs: '50%',
            }}
          >
            <FilterCriteriaWidget
              {...{
                context: 'exit',
                criteria: actionPath?.criteria,
                criteriaMethod: actionPath?.exitCriteriaMethod,
                filterKey: 'exitCriteria',
              }}
            />
          </Grid.Col>,
        ]}

        <Grid.Col
          css={{ paddingTop: 'var(--abyss-space-lg)' }}
          span={{
            xs: actionPath?.manualAssociation === true ? '50%' : '100%',
          }}
        >
          <RemediationActionsWidget
            assignments={actionPath?.assignments}
            initiativeType={actionPath?.initiativeType}
            remediationType={actionPath?.remediationMethod}
            scopeCode={actionPath?.actionPathScopeCode}
          />
        </Grid.Col>
      </Grid>

      {hasCipSource && (
        <Grid>
          <Grid.Col
            css={{ paddingTop: 'var(--abyss-space-lg)' }}
            span={{
              xs: '50%',
            }}
          >
            <SourceRecordsRemediatedWidget data={actionPathStatistics?.remediationStats || {}} />
          </Grid.Col>
          <Grid.Col
            css={{ paddingTop: 'var(--abyss-space-lg)' }}
            span={{
              xs: '50%',
            }}
          >
            <RecordAgeInPathWidget data={actionPathStatistics?.recordAgeStats || {}} />
          </Grid.Col>
        </Grid>
      )}

      <Grid>
        <Grid.Col
          css={{ paddingTop: 'var(--abyss-space-lg)' }}
          span={{
            xs: '50%',
          }}
        >
          <NotesWidget actionPath={actionPath} />
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}
